import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Skull, Swords, Trophy, Clock, Loader2, Crown, Zap } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { SkeletonBossHero, SkeletonList } from '../components/Skeleton';

const TIER_COLOR = {
  elite: '#8ECAE6', legendary: '#E9C46A', mythic: '#F43F5E',
};

export default function WorldBossDetail() {
  const { bossId } = useParams();
  const nav = useNavigate();
  const [boss, setBoss] = useState(null);
  const [myPart, setMyPart] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [cards, setCards] = useState([]);
  const [pickedCardId, setPickedCardId] = useState('');
  const [attacking, setAttacking] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cooldownEnd, setCooldownEnd] = useState(0);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(() => {
    Promise.all([
      api.get(`/world-bosses/${bossId}`).then(r => r.data).catch(() => null),
      api.get(`/world-bosses/${bossId}/leaderboard?limit=30`).then(r => r.data.leaderboard).catch(() => []),
      api.get('/cards?limit=50').then(r => r.data).catch(() => []),
    ]).then(([b, lb, c]) => {
      if (b) { setBoss(b.boss); setMyPart(b.my_participation); }
      setLeaderboard(lb || []);
      setCards(Array.isArray(c) ? c : []);
      setLoading(false);
    });
  }, [bossId]);

  useEffect(() => { load(); }, [load]);

  // Live-refresh boss + ticker
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    const p = setInterval(load, 12000);
    return () => { clearInterval(t); clearInterval(p); };
  }, [load]);

  const attack = async () => {
    if (!pickedCardId) { toast.error('Pick a card first.'); return; }
    if (now < cooldownEnd) return;
    setAttacking(true);
    try {
      const res = await api.post(`/world-bosses/${bossId}/attack`, { card_id: pickedCardId });
      setBoss(res.data.boss);
      // 30s cooldown local UX
      setCooldownEnd(Date.now() + 30000);
      const dmg = res.data.applied_damage;
      toast.success(`Dealt ${dmg} damage!`);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Attack failed';
      toast.error(msg);
      // If server responds with cooldown, parse seconds
      const m = /(\d+)s/.exec(msg);
      if (m) setCooldownEnd(Date.now() + parseInt(m[1], 10) * 1000);
    } finally {
      setAttacking(false);
    }
  };

  if (loading || !boss) return (
    <div className="pb-24 space-y-5 pt-2" data-testid="boss-detail-loading">
      <SkeletonBossHero />
      <div className="h-11 rounded-xl bg-white/5 animate-pulse" />
      <SkeletonList rows={5} avatar />
    </div>
  );

  const tierColor = TIER_COLOR[boss.tier] || '#E9C46A';
  const pct = Math.max(0, Math.min(100, (boss.hp_current / boss.hp_total) * 100));
  const timeLeftMs = (() => { try { return new Date(boss.expires_at).getTime() - now; } catch { return 0; } })();
  const isActive = boss.status === 'active' && timeLeftMs > 0;
  const cdMs = Math.max(0, cooldownEnd - now);

  return (
    <div className="pb-24 space-y-5" data-testid={`world-boss-detail-${boss.boss_id}`}>
      <div className="flex items-center gap-2 pt-2">
        <button onClick={() => nav('/world-bosses')} data-testid="boss-back-btn" className="p-2 rounded-full hover:bg-white/5">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest font-mono" style={{ color: tierColor }}>
            {boss.tier} · {boss.status}
          </p>
          <h1 className="font-serif text-2xl truncate" data-testid="boss-name">{boss.name}</h1>
        </div>
      </div>

      <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-black via-black to-rose-950/50">
        {boss.image_url && (
          <img src={boss.image_url} alt="" className="w-full aspect-video object-cover opacity-70" />
        )}
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Skull className="w-5 h-5" style={{ color: tierColor }} />
            <span className="font-serif text-lg">{boss.hp_current.toLocaleString()} HP</span>
            <span className="text-[10px] text-muted-foreground">/ {boss.hp_total.toLocaleString()}</span>
          </div>
          <div className="h-3 bg-black/60 rounded-full overflow-hidden">
            <motion.div initial={{ width: '100%' }} animate={{ width: `${pct}%` }}
              transition={{ duration: 0.5 }}
              className="h-full" style={{ background: `linear-gradient(90deg, ${tierColor}, #F43F5E)` }} data-testid="boss-hp-bar" />
          </div>
          <div className="flex items-center justify-between text-[10px] font-mono text-white/60">
            <span>{boss.total_participants} raiders</span>
            <span>{Math.round(timeLeftMs / 3600000)}h remaining</span>
          </div>
        </div>
      </div>

      {isActive && (
        <section data-testid="boss-attack-panel">
          <p className="text-[10px] uppercase tracking-widest text-rose-300 mb-2">Deal damage</p>
          <div className="rounded-2xl border border-rose-500/20 bg-gradient-to-br from-rose-950/40 to-black p-3 space-y-2">
            <select
              value={pickedCardId}
              onChange={(e) => setPickedCardId(e.target.value)}
              data-testid="attack-card-picker"
              className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">— select a battle card —</option>
              {cards.map((c) => (
                <option key={c.card_id} value={c.card_id}>
                  {c.species_common_name || c.species_scientific_name} · atk {c.stats?.attack || 0} · {c.rarity}
                </option>
              ))}
            </select>
            <button
              onClick={attack}
              disabled={!pickedCardId || attacking || cdMs > 0}
              data-testid="boss-attack-btn"
              className="w-full rounded-lg bg-rose-500 hover:bg-rose-400 disabled:bg-white/5 disabled:text-muted-foreground text-white font-semibold text-sm py-2.5 flex items-center justify-center gap-2 transition"
            >
              {attacking ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Striking…</>)
                : cdMs > 0 ? (<><Clock className="w-3.5 h-3.5" /> Cooldown {(cdMs / 1000).toFixed(0)}s</>)
                : (<><Swords className="w-3.5 h-3.5" /> STRIKE!</>)}
            </button>
            {myPart && (
              <p className="text-[10px] text-center text-white/60 pt-1">
                You've dealt <span className="font-mono text-rose-300">{myPart.damage_dealt}</span> total damage across {myPart.hits} hit{myPart.hits === 1 ? '' : 's'}.
              </p>
            )}
          </div>
        </section>
      )}

      {!isActive && myPart && !myPart.rewards_claimed && (
        <ClaimPanel bossId={boss.boss_id} onClaimed={load} />
      )}

      <section data-testid="boss-leaderboard">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
          Damage leaderboard
        </p>
        {leaderboard.length === 0 ? (
          <div className="rounded-2xl border border-white/5 bg-card p-4 text-center text-[11px] text-muted-foreground" data-testid="boss-leaderboard-empty">
            No hits landed yet — be the first to strike!
          </div>
        ) : (
          <div className="rounded-2xl border border-white/5 bg-card divide-y divide-white/5 overflow-hidden">
            {leaderboard.map((row, i) => (
              <motion.div
                key={row.user_id}
                initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.02 }}
                className="flex items-center gap-3 px-3 py-2"
                data-testid={`boss-lb-row-${i}`}
              >
                <span className={`w-5 text-center font-mono text-xs ${i < 3 ? 'text-amber-300' : 'text-muted-foreground'}`}>
                  {i === 0 ? <Crown className="w-4 h-4 inline text-amber-400" /> : `#${i + 1}`}
                </span>
                {row.picture ? (
                  <img src={row.picture} alt="" className="w-7 h-7 rounded-full object-cover border border-white/10" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-white/5 border border-white/10" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{row.display_name}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {row.hits} hit{row.hits === 1 ? '' : 's'} · {(row.damage_share * 100).toFixed(1)}%
                  </p>
                </div>
                <span className="font-mono text-sm text-rose-300">{row.damage_dealt}</span>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ClaimPanel({ bossId, onClaimed }) {
  const [claiming, setClaiming] = useState(false);
  const handle = async () => {
    setClaiming(true);
    try {
      const res = await api.post(`/world-bosses/${bossId}/claim`);
      toast.success(`Claimed ${res.data.coins} coins as ${res.data.tier}!`);
      onClaimed?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not claim');
    } finally {
      setClaiming(false);
    }
  };
  return (
    <button
      onClick={handle}
      disabled={claiming}
      data-testid="boss-claim-btn"
      className="w-full rounded-2xl bg-gradient-to-r from-amber-500 to-yellow-400 text-black px-4 py-3 font-semibold text-sm flex items-center justify-center gap-2 hover:from-amber-400 hover:to-yellow-300 shadow-md disabled:opacity-50"
    >
      <Trophy className="w-4 h-4" />
      {claiming ? 'Claiming…' : 'Claim Raid Rewards'}
    </button>
  );
}
